// NodeCalendarsBar.jsx

import React, { useMemo, useRef, useState } from 'react';

// Context
import { useCalendarTimeline } from 'context/CalendarTimelineContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  buildViewSelectorOptions,
  getActiveSelectorLabel,
  getActiveSelectorOption,
} from 'utilities/ViewSelectorOptions';
import { getCalendarViewTypeIconKey } from 'utilities/ViewTypeIcons';

// CSS
import './NodeCalendarsBar.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Popup from 'components/popup/Popup';
import CalendarsPopupContents from 'node/calendars/popup/CalendarsPopupContents';

/**
 * NodeCalendarsBar
 * 
 * Renders the views bar for a node, allowing users to switch between different views.
 * 
 * @param {object[]} views - Optional array of calendar view options.
 * 
 * @returns {ReactNode} NodeCalendarsBar component.
 */
const DEFAULT_CALENDAR_VIEWS = [
  { id: 'MONTH', label: 'Month', type: 'MONTH' },
  { id: 'WEEK', label: 'Week', type: 'WEEK' },
  { id: 'THREEDAY', label: 'Three Day', type: 'THREEDAY' },
  { id: 'DAY', label: 'Day', type: 'DAY' },
  { id: 'AGENDA', label: 'Agenda', type: 'AGENDA' },
];

const NodeCalendarsBar = React.memo(({ views }) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();
  const { calendarViewType, setCalendarViewType } = useCalendarTimeline();

  // ----- REFS ----- //

  const anchorRef = useRef(null);

  // ----- STATE ----- //

  const [popupOpen, setPopupOpen] = useState(false);

  // ----- MEMOS ----- //

  /**
   * Memoize the options for the select input.
   */

  const options = useMemo(() => {
    if (Array.isArray(views) && views.length > 0) {
      return buildViewSelectorOptions(views, { fallbackType: 'MONTH' });
    }
    return DEFAULT_CALENDAR_VIEWS;
  }, [views]);

  const activeViewLabel = useMemo(() => {
    return getActiveSelectorLabel(options, calendarViewType, 'Calendar View');
  }, [options, calendarViewType]);

  const activeViewIconKey = useMemo(() => {
    const match = getActiveSelectorOption(options, calendarViewType);
    return getCalendarViewTypeIconKey(match?.type);
  }, [options, calendarViewType]);

  // ----- HANDLERS ----- //

  /**
   * handleChange
   * 
   * Handle changes to the select input, calling the appropriate handler based on the selected option.
   * 
   * @param {Event} event - The change event from the select input.
   */
  /**
 * Handles change.
 *
 * @param {*} event
 */

  const handleChange = (event) => {
    const nextValue = event?.target?.value || '';
    if (nextValue) {
      setCalendarViewType(nextValue);
    }
  };

  /**
 * Handles popup select.
 *
 * @param {*} optionId
 */
  const handlePopupSelect = (optionId) => {
    setPopupOpen(false);
    handleChange({ target: { value: optionId } });
  };

  // ----- RENDER ----- //

  return (
    <>
      <div className="node-calendars-bar-container">

        {/* VIEWS POPUP BUTTON */}
        <div
          className="node-calendars-bar-views-popup-button"
          ref={anchorRef}
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          aria-expanded={popupOpen}
          aria-label="Calendar span"
          onClick={() => setPopupOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setPopupOpen((prev) => !prev);
            }
          }}
        >
          {activeViewIconKey ? (
            <Icon
              iconKey={activeViewIconKey}
              width={16}
              height={16}
              color={theme.foregroundMuted}
            />
          ) : null}

          {/* VIEWS POPUP BUTTON */}
          <div className="node-calendars-bar-views-popup-button-text">
            {activeViewLabel}
          </div>
          <span
            className="node-calendars-bar-views-popup-button-chevron"
            aria-hidden="true"
          >
            <Icon
              iconKey="keyboard_arrow_down"
              width={18}
              height={18}
              color={theme.foregroundMuted}
            />
          </span>
        </div>

      </div>

      <Popup anchorRef={anchorRef} isOpen={popupOpen} setOpen={setPopupOpen}>
        <CalendarsPopupContents
          options={options}
          selectedDateTypeId={calendarViewType}
          onSelectOption={handlePopupSelect}
        />
      </Popup>
    </>
  );
});

// Dev Tools
NodeCalendarsBar.displayName = 'NodeCalendarsBar';

export default NodeCalendarsBar;
